"use client"

import { FC } from "react"
import { Provider } from "react-redux"
import { store } from "@/src/store"
import Header from "@/src/components/Header"
import Footer from "@/src/components/Footer"
import Form from "@/src/components/ContactForm/Form"
import { Phone, Mail, MapPin, Clock, Car, FileText, CheckCircle } from "lucide-react"
import { FaTelegram, FaViber, FaWhatsapp } from "react-icons/fa"
import { ContentProvider } from "@/src/context/ContentContext"
import { useContent } from "@/src/context/ContentContext"

const ContactsInner: FC = () => {
  const { contacts } = useContent()

  const cards = [
    {
      icon: Phone,
      title: "Телефон",
      value: contacts.phone,
      sub: contacts.workHours,
      href: contacts.phoneHref,
      label: "Зателефонувати",
    },
    {
      icon: Mail,
      title: "Email",
      value: contacts.email,
      sub: "Відповідаємо протягом дня",
      href: contacts.emailHref,
      label: "Написати листа",
    },
    {
      icon: MapPin,
      title: "Адреса",
      value: contacts.address,
      sub: "Офіс в центрі міста",
      href: contacts.mapHref,
      label: "Відкрити на картах",
      external: true,
    },
  ]

  const messengers = [
    {
      label: "Telegram",
      href: `https://t.me/${contacts.phoneHref.replace("tel:+", "")}`,
      icon: FaTelegram,
      bg: "bg-[#229ED9]",
    },
    {
      label: "Viber",
      href: `viber://chat?number=${contacts.phoneHref.replace("tel:", "")}`,
      icon: FaViber,
      bg: "bg-[#7360F2]",
    },
    {
      label: "WhatsApp",
      href: `https://wa.me/${contacts.phoneHref.replace("tel:+", "")}`,
      icon: FaWhatsapp,
      bg: "bg-[#25D366]",
    },
  ]

  return (
    <main>
      {/* ── Hero ── */}
      <section className="pt-36 pb-20 md:pt-48 md:pb-24 text-center px-4 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-white/[0.03] rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 container mx-auto max-w-3xl">
          <span className="inline-block text-xs font-light tracking-[0.2em] uppercase text-gray-500 border border-[#373b42] rounded-full px-4 py-1.5 mb-6">
            Зв&apos;яжіться з нами
          </span>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold text-white mb-6 tracking-tight">
            Контакти
          </h1>
          <p className="text-gray-400 text-lg md:text-xl font-light leading-relaxed max-w-xl mx-auto">
            Безкоштовно приїдемо на об&apos;єкт і розрахуємо вартість ремонту
          </p>
          <div className="mt-10 flex justify-center">
            <div className="h-px w-24 bg-gradient-to-r from-transparent via-[#4a4e55] to-transparent" />
          </div>
        </div>
      </section>

      {/* ── Contact Cards ── */}
      <section className="py-12 md:py-16 px-4" aria-label="Контактні дані">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {cards.map(({ icon: Icon, title, value, sub, href, label, external }) => (
              <div
                key={title}
                className="group flex flex-col bg-[#262a2f] p-7 rounded-2xl border border-[#373b42] shadow-[0_0_6px_#1b1d22] hover:shadow-[0_0_14px_#2c2f35] hover:border-[#4a4e55] hover:-translate-y-1 transition-all duration-300 relative overflow-hidden"
              >
                <div className="flex items-center gap-4 mb-5">
                  <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-[#363a40] group-hover:bg-[#44484f] transition-colors flex-shrink-0">
                    <Icon className="w-6 h-6 text-white" aria-hidden="true" />
                  </div>
                  <span className="text-gray-400 text-sm font-light">{title}</span>
                </div>
                <p className="text-white font-medium text-lg mb-1 leading-snug">{value}</p>
                <p className="text-gray-500 text-sm font-light mb-7">{sub}</p>
                <a
                  href={href}
                  target={external ? "_blank" : undefined}
                  rel={external ? "noopener noreferrer" : undefined}
                  className="mt-auto inline-block text-center px-4 py-2.5 border border-[#4a4e55] text-gray-300 text-sm font-light rounded-lg hover:bg-white hover:text-black hover:border-white transition-all duration-200"
                >
                  {label}
                </a>
                <div className="absolute bottom-0 left-0 w-full h-[3px] bg-gradient-to-r from-[#545860] to-[#6a6f78] group-hover:from-white/40 group-hover:to-white/60 transition-all duration-300" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Schedule + Messengers ── */}
      <section className="py-12 md:py-16 px-4" aria-label="Графік роботи та месенджери">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
            {/* Schedule */}
            <div className="bg-[#262a2f] p-7 rounded-2xl border border-[#373b42] shadow-[0_0_6px_#1b1d22] relative overflow-hidden">
              <div className="flex items-center gap-3 mb-7">
                <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#363a40]">
                  <Clock className="w-5 h-5 text-white" aria-hidden="true" />
                </div>
                <h2 className="text-white font-medium text-xl">Графік роботи</h2>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-[#373b42]">
                  <span className="text-gray-400 font-light text-sm">Понеділок – Субота</span>
                  <span className="text-white font-medium text-sm">{contacts.workHours}</span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-gray-400 font-light text-sm">Неділя</span>
                  <span className="text-white font-medium text-sm">Вихідний</span>
                </div>
              </div>
              <div className="mt-6 flex items-center gap-2.5 bg-[#1e2126] rounded-xl px-4 py-3">
                <Car className="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-white text-xs font-medium">Виїзд на об&apos;єкт</p>
                  <p className="text-gray-500 text-xs font-light">Безкоштовно, протягом 24 годин</p>
                </div>
                <CheckCircle className="w-4 h-4 text-green-500 ml-auto flex-shrink-0" aria-hidden="true" />
              </div>
              <div className="absolute bottom-0 left-0 w-full h-[3px] bg-gradient-to-r from-[#545860] to-[#6a6f78]" />
            </div>

            {/* Messengers */}
            <div className="bg-[#262a2f] p-7 rounded-2xl border border-[#373b42] shadow-[0_0_6px_#1b1d22] relative overflow-hidden">
              <div className="flex items-center gap-3 mb-7">
                <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#363a40]">
                  <Phone className="w-5 h-5 text-white" aria-hidden="true" />
                </div>
                <h2 className="text-white font-medium text-xl">Написати або зателефонувати</h2>
              </div>
              <div className="space-y-3">
                {messengers.map(({ label, href, icon: Icon, bg }) => (
                  <a
                    key={label}
                    href={href}
                    target={href.startsWith("viber:") ? undefined : "_blank"}
                    rel="noopener noreferrer"
                    className={`group/btn flex items-center gap-4 px-5 py-4 rounded-xl ${bg} hover:opacity-90 hover:-translate-y-0.5 transition-all duration-200`}
                    aria-label={`Написати в ${label}`}
                  >
                    <Icon className="w-5 h-5 text-white flex-shrink-0" aria-hidden="true" />
                    <span className="text-white font-light text-sm">{label}</span>
                    <span className="ml-auto text-white/60 text-xs font-light group-hover/btn:text-white/90 transition-colors">→</span>
                  </a>
                ))}
                <a
                  href={contacts.phoneHref}
                  className="group/btn flex items-center gap-4 px-5 py-4 rounded-xl bg-white hover:bg-gray-100 hover:-translate-y-0.5 transition-all duration-200"
                  aria-label="Зателефонувати нам"
                >
                  <Phone className="w-5 h-5 text-black flex-shrink-0" aria-hidden="true" />
                  <span className="text-black font-light text-sm">Зателефонувати нам</span>
                  <span className="ml-auto text-black/40 text-xs font-light group-hover/btn:text-black/70 transition-colors">→</span>
                </a>
              </div>
              <div className="absolute bottom-0 left-0 w-full h-[3px] bg-gradient-to-r from-[#545860] to-[#6a6f78]" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Form ── */}
      <section className="py-12 md:py-20 px-4" aria-labelledby="contacts-form-title">
        <div className="container mx-auto max-w-2xl">
          <div className="flex flex-col items-center mb-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#4a4e55]" />
              <FileText className="w-6 h-6 text-gray-400" aria-hidden="true" />
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#4a4e55]" />
            </div>
            <h2 id="contacts-form-title" className="text-3xl lg:text-4xl font-semibold text-white text-center">
              Залишити заявку
            </h2>
            <p className="text-gray-500 text-sm font-light mt-2">Передзвонимо протягом 30 хвилин</p>
          </div>
          <Form />
        </div>
      </section>
    </main>
  )
}

const ContactsPage: FC = () => {
  return (
    <Provider store={store}>
      <ContentProvider>
        <div
          className="min-h-screen"
          style={{ background: "linear-gradient(to bottom, #181b20 0%, #111216 25%, #181b20 55%, #1e2126 100%)" }}
        >
          <Header />
          <ContactsInner />
          <Footer />
        </div>
      </ContentProvider>
    </Provider>
  )
}

export default ContactsPage
